import CONFIG from "./../config.json";
import { cacheGetCacheTime, IfQuickReloadFlushEverything } from "./cache.js";
import { posterCreateAndAppend, posterRender, posterAppendAuthor, posterParse } from "./poster.js";
import {
    postersGetPosterResposiveWidth,
    postersSetExcludedPosterIds,
    postersSetIndexMode,
    postersUnescapeSpacesAndApostrophes
} from "./posters.js";
import { controlStartUp } from "./control.js";
import { empGetEditorUrl } from "./emp.js";
import { ADSENSE_MAXIMUM_WIDTH, adsenseAddAndPush, adsensePushContainerAds } from "./adsense.js";
import {
    pageFetchPosters,
    pageGetNextPageHref,
    pageIsThereMorePosters
} from "./page.js";
import { loadMoreBind } from "./load-more.js";

const SEO_LABEL_STORAGE_PREFIX = "seoLabelPosters:";
let seoLabelCursor = null;
let seoLabelPendingPosters = [];
let seoLabelFinished = false;

function seoLabelDateToApiPath(date) {
    return date.getDate() + "/" + (date.getMonth() + 1);
}

function seoLabelMatchesPlace(poster, vegueria, town) {
    const regions = Array.isArray(window.seoLabelRegions) ? window.seoLabelRegions : [];
    if (regions.length > 0 && !regions.includes(poster.region)) {
        return false;
    }
    return town === "" || poster.town === town;
}

function seoLabelHasLabel(poster) {
    const labels = Array.isArray(window.seoLabelLabels) ? window.seoLabelLabels : [];
    const posterLabels = Array.isArray(poster.labelsList) ? poster.labelsList : [];
    return posterLabels.some((label) => labels.includes(label));
}

function seoLabelFetchBatch(cursor = null) {
    const today = new Date();
    let resource = `${CONFIG.posters_api_url}/resource/poster/public/from/${seoLabelDateToApiPath(today)}`;
    let separator = "?";
    if (cursor !== null && cursor !== "") {
        resource += separator + "cursor=" + encodeURIComponent(cursor);
        separator = "&";
    }
    const cacheTime = cacheGetCacheTime();
    if (cacheTime !== null && cacheTime !== "") {
        resource += separator + "cacheTime=" + cacheTime;
    }

    return fetch(resource, {headers: {"X-Cartells-Frontend-Client": "EMP_POSTERS"}})
            .then((response) => response.json());
}

async function seoLabelCollectPosters(limit, vegueria, town, excludedIds) {
    const posters = [];
    const excluded = new Set(excludedIds);

    while (posters.length < limit && !seoLabelFinished) {
        if (seoLabelPendingPosters.length === 0) {
            const payload = await seoLabelFetchBatch(seoLabelCursor);
            seoLabelPendingPosters = Array.isArray(payload.list) ? payload.list.slice() : [];
            seoLabelCursor = payload.cursor || "";
            if (seoLabelPendingPosters.length === 0 && seoLabelCursor === "") {
                seoLabelFinished = true;
                break;
            }
        }

        while (seoLabelPendingPosters.length > 0 && posters.length < limit) {
            const poster = seoLabelPendingPosters.shift();
            if (poster.draft === true || excluded.has(poster.id)) {
                continue;
            }
            if (seoLabelMatchesPlace(poster, vegueria, town) && seoLabelHasLabel(poster)) {
                posters.push(poster);
                excluded.add(poster.id);
            }
        }

        if (seoLabelPendingPosters.length === 0 && seoLabelCursor === "") {
            seoLabelFinished = true;
        }
    }

    return posters;
}

function seoLabelBindMainLoadMore(wrapper, vegueria, town) {
    const buttonWrapper = document.getElementById("mainNextPageButtonWrapper");
    const button = document.getElementById("mainNextPageButton");
    if (!buttonWrapper || !button) {
        return;
    }

    const renderedPosters = seoLabelGetRenderedPosterIds(wrapper).length;
    buttonWrapper.style.display = !seoLabelFinished && renderedPosters >= 20
            ? "block"
            : "none";
    loadMoreBind(button, async () => {
        const posters = await seoLabelCollectPosters(
                20,
                vegueria,
                town,
                seoLabelGetRenderedPosterIds(wrapper)
                );
        seoLabelAppendPosterList(posters, wrapper);
        if (posters.length === 0 || seoLabelFinished) {
            buttonWrapper.style.display = "none";
        }
    });
}

function seoLabelGetGroupId() {
    return window.seoLabelGroupId || "family";
}

function seoLabelGetStorageKey(vegueria, town) {
    const cacheTime = cacheGetCacheTime() || "default";
    return [
        SEO_LABEL_STORAGE_PREFIX,
        cacheTime,
        seoLabelGetGroupId(),
        vegueria,
        town
    ].join(":");
}

function seoLabelReadCachedPosters(storageKey) {
    try {
        const cached = sessionStorage.getItem(storageKey);
        if (!cached) {
            return null;
        }
        const parsed = JSON.parse(cached);
        return Array.isArray(parsed) ? parsed : null;
    } catch (err) {
        return null;
    }
}

function seoLabelWriteCachedPosters(storageKey, posters) {
    try {
        sessionStorage.setItem(storageKey, JSON.stringify(posters));
    } catch (err) {
        // Ignore storage failures.
    }
}

function seoLabelClearStaleCachedPosters(activeKey) {
    try {
        for (let i = sessionStorage.length - 1; i >= 0; i--) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith(SEO_LABEL_STORAGE_PREFIX) && key !== activeKey) {
                sessionStorage.removeItem(key);
            }
        }
    } catch (err) {
        // Ignore storage failures.
    }
}

function seoLabelShowEmptyMessage(wrapper, town, vegueria) {
    const element = document.createElement("div");
    element.setAttribute("id", "mainNoPostersMessage");
    const place = town === "" ? vegueria : town;
    element.innerHTML =
            "No hi ha cartells amb aquestes etiquetes a <b class='nowrap'>" +
            place +
            "</b>, publica el teu cartell a <a class='nowrap' href='" + empGetEditorUrl() + "'>editor.yuppiya.com</a>";
    wrapper.appendChild(element);
}

function seoLabelAppendPosterList(posters, wrapper) {
    let adsNumber = 0;
    posters.forEach((poster, index) => {
        posterCreateAndAppend(poster, wrapper);
        const posterNumber = index + 1;
        if (Math.floor(posterNumber / 3) > adsNumber) {
            adsNumber++;
            adsenseAddAndPush(
                    wrapper,
                    postersGetPosterResposiveWidth(ADSENSE_MAXIMUM_WIDTH)
                    );
        }
    });
}

function seoLabelEnsureListingAd(wrapper) {
    if (wrapper.getElementsByClassName("adWrapper").length > 0) {
        return;
    }
    const fallback = document.querySelector(".mainAdWrapper");
    if (fallback) {
        adsenseAddAndPush(fallback);
    }
}

function seoLabelGetRenderedPosterIds(wrapper) {
    return Array.from(wrapper.getElementsByClassName("posterWrapper"))
            .map((poster) => poster.getAttribute("data-post-id"))
            .filter((id) => id !== null && id !== "");
}

function seoLabelHideOtherPosters() {
    const section = document.getElementById("mainOtherPosters");
    if (section) {
        section.style.display = "none";
    }
}

function seoLabelShowOtherPosters(excludedIds) {
    const section = document.getElementById("mainOtherPosters");
    const wrapper = document.getElementById("mainOtherPostersWrapper");
    if (!section || !wrapper) {
        return Promise.resolve();
    }

    const hasLoadedPosters = wrapper.getElementsByClassName("posterWrapper").length > 0;
    if (wrapper.dataset.postersLoaded === "true") {
        if (hasLoadedPosters || excludedIds.length >= 20) {
            section.style.display = hasLoadedPosters ? "block" : "none";
            posterRender(wrapper);
            Array.from(wrapper.getElementsByClassName("posterWrapper")).forEach((poster) => {
                posterAppendAuthor(poster);
                posterParse(poster);
            });
            return Promise.resolve();
        }
        wrapper.dataset.postersLoaded = "false";
    }

    const hasPlaceFilter = window.seoLabelHasPlaceFilter === true;
    const regions = Array.isArray(window.seoLabelRegions) ? window.seoLabelRegions : [];
    if (regions.length === 0 && hasPlaceFilter) {
        seoLabelHideOtherPosters();
        return Promise.resolve();
    }

    const regionFilter = hasPlaceFilter ? regions : "";
    const remainingSlots = Math.max(0, 20 - excludedIds.length);
    if (remainingSlots <= 0) {
        seoLabelHideOtherPosters();
        return Promise.resolve();
    }
    const targetPostersNumber = hasPlaceFilter ? Math.max(5, remainingSlots) : remainingSlots;

    section.style.display = "block";
    controlStartUp("mainOtherSentinel");
    postersSetExcludedPosterIds(excludedIds);
    postersSetIndexMode("others");

    const params = new URL(document.location).searchParams;
    let otherIndex = parseInt(params.get("otherIndex"), 10);
    if (Number.isNaN(otherIndex)) {
        otherIndex = 0;
    }
    const otherCursor = params.get("otherCursor") || "";

    return pageFetchPosters(
            wrapper,
            regionFilter,
            "",
            "",
            otherIndex,
            otherCursor,
            null,
            {
                forceFullPage: true,
                targetPostersNumber,
                skipPoster: seoLabelHasLabel
            }
            ).then(() => {
        const nextWrapper = document.getElementById("mainOtherNextPageButtonWrapper");
        const nextButton = document.getElementById("mainOtherNextPageButton");
        if (!nextWrapper || !nextButton) {
            return;
        }
        const hasPosters = wrapper.getElementsByClassName("posterWrapper").length > 0;
        section.style.display = hasPosters ? "block" : "none";
        if (hasPosters) {
            adsensePushContainerAds(section);
        }
        if (pageIsThereMorePosters()) {
            nextWrapper.style.display = "block";
            nextButton.setAttribute("href", pageGetNextPageHref("otherIndex", true, "otherCursor"));
            loadMoreBind(nextButton, async () => {
                const href = nextButton.getAttribute("href");
                if (!href) {
                    return;
                }
                const nextUrl = new URL(href, window.location.origin);
                const nextIndex = parseInt(nextUrl.searchParams.get("otherIndex"), 10);
                const nextCursor = nextUrl.searchParams.get("otherCursor") || "";
                postersSetExcludedPosterIds(excludedIds);
                postersSetIndexMode("others");
                try {
                    await pageFetchPosters(
                            wrapper,
                            regionFilter,
                            "",
                            "",
                            Number.isNaN(nextIndex) ? 0 : nextIndex,
                            nextCursor,
                            null,
                            {skipPoster: seoLabelHasLabel}
                            );
                    if (pageIsThereMorePosters()) {
                        nextButton.setAttribute("href", pageGetNextPageHref("otherIndex", true, "otherCursor"));
                    } else {
                        nextWrapper.style.display = "none";
                        nextButton.removeAttribute("href");
                    }
                } finally {
                    postersSetExcludedPosterIds([]);
                }
            });
        } else {
            nextWrapper.style.display = "none";
            nextButton.removeAttribute("href");
        }
    }).finally(() => {
        wrapper.dataset.postersLoaded = "true";
        postersSetExcludedPosterIds([]);
    });
}

async function seoLabelStartUp(vegueriaValue, townValue) {
    const wrapper = document.getElementById("mainPostersWrapper");
    const loader = document.getElementById("mainLoader");
    if (!wrapper || wrapper.dataset.postersLoaded === "true") {
        posterRender(wrapper);
        Array.from(wrapper.getElementsByClassName("posterWrapper")).forEach((poster) => {
            posterAppendAuthor(poster);
            posterParse(poster);
        });
        seoLabelEnsureListingAd(wrapper);
        seoLabelShowOtherPosters(seoLabelGetRenderedPosterIds(wrapper));
        return;
    }

    const vegueria = postersUnescapeSpacesAndApostrophes(vegueriaValue || "");
    const town = postersUnescapeSpacesAndApostrophes(townValue || "");

    if (loader) {
        loader.style.display = "inline-block";
    }

    await IfQuickReloadFlushEverything();

    const storageKey = seoLabelGetStorageKey(vegueria, town);
    seoLabelClearStaleCachedPosters(storageKey);

    const cachedPosters = seoLabelReadCachedPosters(storageKey);
    if (cachedPosters !== null) {
        seoLabelAppendPosterList(cachedPosters, wrapper);
        seoLabelEnsureListingAd(wrapper);
        if (cachedPosters.length === 0) {
            seoLabelShowEmptyMessage(wrapper, town, vegueria);
        }
        wrapper.dataset.postersLoaded = "true";
        if (loader) {
            loader.style.display = "none";
        }
        seoLabelBindMainLoadMore(wrapper, vegueria, town);
        await seoLabelShowOtherPosters(cachedPosters.map((poster) => poster.id).filter(Boolean));
        return;
    }

    const matchingPosters = await seoLabelCollectPosters(20, vegueria, town, []);

    seoLabelWriteCachedPosters(storageKey, matchingPosters);
    seoLabelAppendPosterList(matchingPosters, wrapper);
    seoLabelEnsureListingAd(wrapper);

    if (matchingPosters.length === 0) {
        seoLabelShowEmptyMessage(wrapper, town, vegueria);
    }
    wrapper.dataset.postersLoaded = "true";
    if (loader) {
        loader.style.display = "none";
    }
    seoLabelBindMainLoadMore(wrapper, vegueria, town);
    await seoLabelShowOtherPosters(matchingPosters.map((poster) => poster.id).filter(Boolean));
}

export { seoLabelStartUp };
